import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import {
  analyzeFieldValueBulkRows,
  createBlankFieldValueBulkRow,
  createFieldValueBulkRows,
  getFieldValueTemplateRows,
  mergeImportedFieldValueRows,
  parseFieldValueImportMatrix,
} from './fieldValueBulkUtils';
import {
  isButtonFieldType,
  isNumericFieldType,
  isWholeNumberFieldType,
} from './configurationAdminUtils';

const safeFilenamePart = (value) => String(value || 'field')
  .trim()
  .replace(/[^a-z0-9]+/gi, '-')
  .replace(/^-|-$/g, '')
  .toLowerCase();

const loadSpreadsheetLibrary = () => import('xlsx');

const buildWorkbook = (XLSX, field, values) => {
  const workbook = XLSX.utils.book_new();
  const instructions = XLSX.utils.aoa_to_sheet([
    ['Values bulk upload'],
    ['Selected field', field.fieldName],
    ['ConfigFieldId', field.id],
    ['Field type', field.fieldType],
    [],
    ['Instructions'],
    ['Leave Id blank to create a new value. Use an existing Id only to update that exact row.'],
    ['ConfigFieldId must match the selected field. Rows omitted from an upload are not deleted.'],
    ['ValueMode accepts Fixed or Range. Range is available for Whole Number and Decimal Number fields.'],
    ['For a range, enter MinValue and MaxValue; OptionValue is stored automatically as min-max.'],
    ['For a Button field, enter a positive whole number in MaxRows.'],
    ['IsDefaultValue accepts TRUE or FALSE. Only one general default is allowed.'],
  ]);
  instructions['!cols'] = [{ wch: 24 }, { wch: 42 }];
  const valuesSheet = XLSX.utils.aoa_to_sheet(getFieldValueTemplateRows(field, values));
  valuesSheet['!cols'] = [
    { wch: 12 },
    { wch: 16 },
    { wch: 14 },
    { wch: 36 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 18 },
  ];
  XLSX.utils.book_append_sheet(workbook, instructions, 'Instructions');
  XLSX.utils.book_append_sheet(workbook, valuesSheet, 'Values');
  return workbook;
};

const readWorkbookMatrix = async (file) => {
  const XLSX = await loadSpreadsheetLibrary();
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = workbook.Sheets.Values || workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) throw new Error('The workbook does not contain a readable sheet.');
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
};

const FieldValueBulkEditor = ({ show, field, values, saving, validation, onClose, onSave }) => {
  const [rows, setRows] = useState([]);
  const [importError, setImportError] = useState('');
  const [importNotice, setImportNotice] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show || !field) return;
    setRows(createFieldValueBulkRows(values, field));
    setImportError('');
    setImportNotice('');
    setPasteText('');
    setShowPaste(false);
  }, [field, show, values]);

  const analysis = useMemo(
    () => field ? analyzeFieldValueBulkRows(rows, values, field) : null,
    [field, rows, values]
  );

  if (!field) return null;

  const isNumericField = isNumericFieldType(field.fieldType);
  const isButtonField = isButtonFieldType(field.fieldType);
  const numericInputStep = isWholeNumberFieldType(field.fieldType) ? '1' : 'any';

  const updateRow = (clientKey, changes) => {
    setRows((current) => current.map((row) => {
      if (row.clientKey !== clientKey) return row;
      const next = { ...row, ...changes };
      if (isNumericField && next.valueMode === 'range') {
        next.optionValue = next.minValue === '' || next.maxValue === '' ? '' : `${next.minValue}-${next.maxValue}`;
        next.isDefaultValue = false;
      }
      if (next.valueMode === 'fixed') {
        next.minValue = '';
        next.maxValue = '';
      }
      return next;
    }));
  };

  const importMatrix = (matrix, sourceLabel) => {
    const imported = parseFieldValueImportMatrix(matrix, field);
    const nextRows = mergeImportedFieldValueRows(rows, imported);
    setRows(nextRows);
    setImportError('');
    setImportNotice(`${imported.length} row${imported.length === 1 ? '' : 's'} imported from ${sourceLabel}. Review validation and changes before saving.`);
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      importMatrix(await readWorkbookMatrix(file), file.name);
    } catch (error) {
      setImportNotice('');
      setImportError(error.message || 'Unable to read the selected file.');
    }
  };

  const handlePasteImport = async () => {
    try {
      const XLSX = await loadSpreadsheetLibrary();
      const workbook = XLSX.read(pasteText, { type: 'string' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
      importMatrix(matrix, 'the pasted table');
      setPasteText('');
    } catch (error) {
      setImportNotice('');
      setImportError(error.message || 'Unable to read the pasted table.');
    }
  };

  const downloadTemplate = async (includeCurrentValues) => {
    try {
      const XLSX = await loadSpreadsheetLibrary();
      const workbook = buildWorkbook(XLSX, field, includeCurrentValues ? values : []);
      const suffix = includeCurrentValues ? 'current-values' : 'upload-template';
      XLSX.writeFile(workbook, `${safeFilenamePart(field.fieldName)}-${suffix}.xlsx`);
    } catch (error) {
      setImportNotice('');
      setImportError(error.message || 'Unable to create the spreadsheet.');
    }
  };

  const hasErrors = analysis.errorCount > 0;

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} size="xl" centered dialogClassName="config-admin-bulk-modal">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Bulk edit and upload values</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="config-admin-bulk-scope">
          <div><small>Field</small><strong>{field.fieldName}</strong></div>
          <div><small>Field ID</small><strong>{field.id}</strong></div>
          <div><small>Type</small><strong>{field.fieldType}</strong></div>
        </div>

        <Alert variant="info" className="small">
          Edit existing rows directly or import new rows with a blank ID. Existing IDs and the selected field are locked, omitted rows remain unchanged, and bulk mode never deletes values.
        </Alert>

        {validation && <Alert variant="danger">{validation}</Alert>}
        {importError && <Alert variant="danger" dismissible onClose={() => setImportError('')}>{importError}</Alert>}
        {importNotice && <Alert variant="success" dismissible onClose={() => setImportNotice('')}>{importNotice}</Alert>}

        <div className="config-admin-bulk-toolbar">
          <div className="d-flex flex-wrap gap-2">
            <Button type="button" variant="outline-primary" onClick={() => downloadTemplate(false)}>
              <i className="bi bi-file-earmark-spreadsheet me-2" aria-hidden="true"></i>Download upload template
            </Button>
            <Button type="button" variant="outline-primary" onClick={() => downloadTemplate(true)} disabled={values.length === 0}>
              <i className="bi bi-download me-2" aria-hidden="true"></i>Export current values
            </Button>
            <Button type="button" variant="outline-primary" onClick={() => fileInputRef.current?.click()}>
              <i className="bi bi-upload me-2" aria-hidden="true"></i>Upload spreadsheet
            </Button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv,.tsv" className="d-none" onChange={handleFile} />
            <Button type="button" variant="outline-primary" onClick={() => setShowPaste((current) => !current)}>
              <i className="bi bi-clipboard me-2" aria-hidden="true"></i>Paste table
            </Button>
          </div>
          <Button type="button" variant="primary" onClick={() => setRows((current) => [...current, createBlankFieldValueBulkRow(field)])}>
            <i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add blank row
          </Button>
        </div>

        {showPaste && (
          <div className="config-admin-bulk-paste">
            <Form.Group>
              <Form.Label>Paste rows including the header row</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                placeholder="Id&#9;ConfigFieldId&#9;ValueMode&#9;OptionValue&#9;MinValue&#9;MaxValue&#9;IsDefaultValue"
              />
              <Form.Text>Copy directly from Excel or paste CSV/TSV content. Matching existing IDs update staged rows; blank IDs add new rows.</Form.Text>
            </Form.Group>
            <div className="mt-2"><Button type="button" size="sm" onClick={handlePasteImport} disabled={!pasteText.trim()}>Add pasted rows</Button></div>
          </div>
        )}

        <div className="table-responsive config-admin-bulk-table-wrap">
          <Table bordered hover className="config-admin-bulk-table mb-0">
            <thead>
              <tr>
                <th>ID</th>
                {isNumericField && <th>Mode</th>}
                <th>{field.fieldType === 'Hyperlink' ? 'Link URL' : isButtonField ? 'Maximum rows' : 'Option value'}</th>
                {isNumericField && <><th>Minimum</th><th>Maximum</th></>}
                <th>General default</th>
                <th>Status</th>
                <th aria-label="Row actions"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowErrors = analysis.errorsByKey[row.clientKey] || [];
                const operation = analysis.operations.find((candidate) => candidate.clientKey === row.clientKey);
                const status = rowErrors.length > 0 ? 'Error' : operation?.method === 'POST' ? 'New' : operation?.method === 'PUT' ? 'Changed' : 'Unchanged';
                return (
                  <React.Fragment key={row.clientKey}>
                    <tr className={rowErrors.length > 0 ? 'table-danger' : ''}>
                      <td><Form.Control value={row.id} disabled aria-label="Value ID" placeholder="New" /></td>
                      {isNumericField && (
                        <td>
                          <Form.Select value={row.valueMode} onChange={(event) => updateRow(row.clientKey, { valueMode: event.target.value })} aria-label="Value mode">
                            <option value="fixed">Fixed</option>
                            <option value="range">Range</option>
                          </Form.Select>
                        </td>
                      )}
                      <td>
                        <Form.Control
                          value={isButtonField ? row.maxClicks : row.optionValue}
                          onChange={(event) => updateRow(row.clientKey, isButtonField
                            ? { maxClicks: event.target.value }
                            : { optionValue: event.target.value })}
                          disabled={isNumericField && row.valueMode === 'range'}
                          type={(isNumericField && row.valueMode === 'fixed') || isButtonField ? 'number' : 'text'}
                          min={isButtonField ? '1' : undefined}
                          step={isButtonField ? '1' : isNumericField ? numericInputStep : undefined}
                          aria-label={isButtonField ? 'Maximum rows' : 'Option value'}
                        />
                      </td>
                      {isNumericField && (
                        <>
                          <td><Form.Control type="number" step={numericInputStep} value={row.minValue} disabled={row.valueMode !== 'range'} onChange={(event) => updateRow(row.clientKey, { minValue: event.target.value })} aria-label="Minimum value" /></td>
                          <td><Form.Control type="number" step={numericInputStep} value={row.maxValue} disabled={row.valueMode !== 'range'} onChange={(event) => updateRow(row.clientKey, { maxValue: event.target.value })} aria-label="Maximum value" /></td>
                        </>
                      )}
                      <td className="text-center"><Form.Check type="checkbox" checked={row.isDefaultValue === true} disabled={isNumericField && row.valueMode === 'range'} onChange={(event) => updateRow(row.clientKey, { isDefaultValue: event.target.checked })} aria-label="General default value" /></td>
                      <td><Badge bg={status === 'Error' ? 'danger' : status === 'New' ? 'success' : status === 'Changed' ? 'warning' : 'light'} text={status === 'Unchanged' || status === 'Changed' ? 'dark' : undefined}>{status}</Badge></td>
                      <td>
                        {!row.isExisting && <Button type="button" size="sm" variant="outline-danger" onClick={() => setRows((current) => current.filter((candidate) => candidate.clientKey !== row.clientKey))} aria-label="Remove new row"><i className="bi bi-trash" aria-hidden="true"></i></Button>}
                      </td>
                    </tr>
                    {rowErrors.length > 0 && (
                      <tr className="config-admin-bulk-error-row"><td colSpan={isNumericField ? 8 : 5}>{rowErrors.join(' ')}</td></tr>
                    )}
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={isNumericField ? 8 : 5} className="text-center text-muted py-4">No values exist. Add a blank row or upload a spreadsheet.</td></tr>}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="config-admin-bulk-footer">
        <div className="config-admin-bulk-summary" aria-live="polite">
          <Badge bg="success">{analysis.createCount} to create</Badge>
          <Badge bg="warning" text="dark">{analysis.updateCount} to update</Badge>
          <Badge bg="light" text="dark">{analysis.unchangedCount} unchanged</Badge>
          {hasErrors && <Badge bg="danger">{analysis.errorCount} validation {analysis.errorCount === 1 ? 'error' : 'errors'}</Badge>}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave({ rows, operations: analysis.operations, baselineValues: values })} disabled={saving || hasErrors || analysis.operations.length === 0}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save {analysis.operations.length} change{analysis.operations.length === 1 ? '' : 's'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default FieldValueBulkEditor;
